import React, { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { MODAL_PAGE_SIZE } from "@shared/utils/constants";
import { Badge } from "@shared/ui/badge";
import { useGetAllRecipientData } from "@features/ux/components/recipientList/hooks";
// import { useGetMembersDetails } from "@features/members/hooks"; // Replace with your actual hook

const MembersList = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // Read search query and page from URL
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const currentPage = parseInt(searchParams.get("page") || "1");

  // Replace this with your actual API hook
  const { data: membersData } = useGetAllRecipientData(
    Number(id),
    currentPage,
    MODAL_PAGE_SIZE,
    searchQuery
  );

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: MODAL_PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination based on filtered data
  useEffect(() => {
    const totalItems = membersData?.count || 0;
    const totalPages = Math.ceil(totalItems / MODAL_PAGE_SIZE);
    const startItem = (currentPage - 1) * MODAL_PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * MODAL_PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: MODAL_PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [membersData?.count, currentPage]);

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }

    // Reset to page 1 on new search
    delete newParams.page;

    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  // Define table columns
  const columns = useMemo<TableColumn<any>[]>(
    () => [
      {
        key: "user",
        header: "Name",
        sortable: true,
       render: (value: string,row:any) => (
        <span className="text-sm text-gray-600">{row?.user?.name || "N/A"}</span>
      ),
      },
      {
        key: "email",
        header: "Email",
        sortable: true,
           render: (value: string,row:any) => (
        <span className="text-sm text-gray-600">{row?.user?.email || "N/A"}</span>
      ),
      },
      {
        key: "phone",
        header: "Phone",
        sortable: true,
        render: (value: string,row:any) => (
        <span className="text-sm text-gray-600">{row?.user?.contactNumber || "N/A"}</span>
      ),
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        render: (value) => (
          <Badge
            variant={value === "Active" ? "default" : "secondary"}
            className={
              value === "Active" ? "bg-green-500 hover:bg-green-600" : ""
            }
          >
            {value}
          </Badge>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      <GenericTable
        data={membersData?.result || membersData?.results || []}
        columns={columns}
        rowKey="id"
        emptyMessage="No members found matching your criteria"
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search members..."
      />
    </div>
  );
};

export default MembersList;
